import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { SkillDetailsStatus } from '@core/positions-and-skills-types';
import {
  BadgeComponent,
  IconComponent,
  type IconName,
  IconToken,
  RtTooltip,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-job-skill-summary-badge',
  templateUrl: './job-skill-summary-badge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BadgeComponent, RtTooltip, IconComponent],
})
export class JobSkillSummaryBadgeComponent {
  private readonly i18n = inject(I18nService);

  protected readonly translation = {
    status: {
      [SkillDetailsStatus.AboveRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.above_required_level',
      ),
      [SkillDetailsStatus.AtRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.at_required_level_full',
      ),
      [SkillDetailsStatus.BelowRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.below_required_level',
      ),
      [SkillDetailsStatus.NoEvaluated]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.not_evaluated',
      ),
      [SkillDetailsStatus.NoRequiredLevel]: this.i18n.translate(
        'app.interview.summary.skills_summary.status.not_required_level',
      ),
    },
    mixedTeamFeedback: this.i18n.translate(
      'app.interview.summary.skills_summary.mixed_team_feedback',
    ),
    notRequiredLevelTooltip: this.i18n.translate(
      'app.interview.summary.skills_summary.no_required_level_tooltip',
    ),
  };
  protected readonly icons = {
    status: {
      [SkillDetailsStatus.AboveRequiredLevel]: {
        name: <IconName>'trend-up',
        color: <IconToken>'colorIconSuccessDefault',
      },
      [SkillDetailsStatus.AtRequiredLevel]: {
        name: <IconName>'check-small',
        color: <IconToken>'colorIconSuccessDefault',
      },
      [SkillDetailsStatus.BelowRequiredLevel]: {
        name: <IconName>'trend-down',
        color: <IconToken>'colorIconCriticalDefault',
      },
      [SkillDetailsStatus.NoEvaluated]: {
        name: <IconName>'remove',
        color: <IconToken>'colorIconLowDefault',
      },
      [SkillDetailsStatus.NoRequiredLevel]: {
        name: <IconName>'help-plain',
        color: <IconToken>'colorIconPrimaryDefault',
      },
    },
    mixedTeamFeedback: {
      name: <IconName>'warning',
      color: <IconToken>'colorIconWarningDefault',
    },
  };

  public readonly status = input<SkillDetailsStatus>();
  public readonly mixedTeamFeedback = input<boolean>(false);

  protected badgeInfo = computed(() => {
    let label = '';
    let iconName = <IconName>'';
    let iconColor = <IconToken>'';
    const status = this.status();
    const mixedTeamFeedback = this.mixedTeamFeedback();
    if (status) {
      label = this.translation.status[status];
      iconName = this.icons.status[status].name;
      iconColor = this.icons.status[status].color;
    } else if (mixedTeamFeedback) {
      label = this.translation.mixedTeamFeedback;
      iconName = this.icons.mixedTeamFeedback.name;
      iconColor = this.icons.mixedTeamFeedback.color;
    }

    return {
      label,
      tooltip:
        status === SkillDetailsStatus.NoRequiredLevel
          ? this.translation.notRequiredLevelTooltip
          : '',
      iconName,
      iconColor,
    };
  });
}
